import locations from './locations'
import phases from './phases'
import phaseBehaviors from './phase_behaviors'
import questions from './questions'
import requirements from './requirements'

const getDefaultState = () => {
  return {
    // The opportunities currently visible
    active: [],
    // Used to indicate when waiting on the server
    loading: false,
    // The search string being applied
    search: '',
    // What filters are currently applied
    filters: {},
    // The total records matching the filter according to the server (for pagination)
    total: 0,
    // The current page being viewed and its size
    page: 1,
    rowsPerPage: 25,
    // Sorting
    sort: null,
    // Application and Eligibility Counts
    counts: {},
  }
}
const state = getDefaultState()

const getters = {
  all: state => state.active,
  active: state => state.active,
  find: state => (id = null) => {
    return state.active.find(opportunity => opportunity.id == (id || Vue.$route.params.id))
  },
  loading: state => state.loading,
  orderedPhases: (state, getters) => {
    const opportunity = getters.find()
    if (opportunity && opportunity.phases && opportunity.phases[0]) {
      let order = 1
      let current = opportunity.phases.find(p => p.initial)
      let phases = [{ id: current.id, name: current.name, description: current.description, order }]

      while (current.next) {
        current = opportunity.phases.find(p => p.name == current.next.name)
        order++
        phases.push({ id: current.id, name: current.name, description: current.description, order })
      }
      return phases
    }
    return []
  },
}

const mutations = {
  resetState(state) {
    Object.assign(state, getDefaultState())
  },

  startLoading(state) {
    state.loading = true
  },

  stopLoading(state) {
    state.loading = false
  },

  setActive(
    state,
    { items = [], page = 1, per = 25, total = 0, search = null, sort = null, filters = {} }
  ) {
    state.search = search
    state.active = items
    state.sort = sort
    state.filters = filters
    state.page = page
    state.rowsPerPage = per
    state.total = total
    state.loading = false
  },

  set(state, opportunity) {
    const find = state.active.find(o => o.id == opportunity.id)
    if (find) state.active.splice(state.active.indexOf(find), 1, opportunity)
    if (!find) state.active.push(opportunity)
  },

  setLocations(state, { opportunity, locations }) {
    const find = state.active.find(o => o.id == opportunity.id)
    if (find)
      state.active.splice(state.active.indexOf(find), 1, Object.assign({}, find, { locations }))
  },

  remove(state, id) {
    state.active = state.active.filter(o => o.id != id)
  },

  setCounts(state, counts) {
    state.counts = Object.assign({}, state.counts, counts)
  },
}

const actions = {
  search(
    { commit, state },
    { search = '', page = 1, rowsPerPage = 25, sortBy = null, descending = true, filters = {} } = {}
  ) {
    this.$authorize('list', 'Opportunity')

    const params = {
      search: search,
      page: page,
      per: rowsPerPage,
      sort: sortBy ? `${sortBy} ${descending ? 'DESC' : 'ASC'}` : null,
      ...filters,
    }

    commit('startLoading')
    return Vue.$http
      .get('admin/opportunities', { params })
      .then(response => commit('setActive', response.data), error => commit('stopLoading'))
  },

  fetch({ commit, getters }, id) {
    this.$authorize('read', 'Opportunity')

    const opportunity = getters.find(id)

    if (opportunity) {
      commit('set', Object.assign(opportunity, { loading: true }))
    } else {
      commit('set', { id, loading: true })
    }

    return Vue.$http.get(`admin/opportunities/${id}`).then(response => {
      return commit('set', Object.assign(response.data, { loading: false }))
    })
  },

  create({ commit }, opportunity) {
    this.$authorize('create', 'Opportunity')

    return Vue.$http.post('admin/opportunities', { opportunity }).then(
      response => {
        commit('set', response.data)
        return response.data
      },
      response => Object.assign(opportunity, response.data)
    )
  },

  update({ commit }, opportunity) {
    this.$authorize('edit', 'Opportunity')

    return Vue.$http
      .patch(`admin/opportunities/${opportunity.id}`, { opportunity })
      .then(
        response => commit('set', response.data),
        response => commit('set', Object.assign(opportunity, response.data))
      )
  },

  touch({ commit }, id) {
    this.$authorize('edit', 'Opportunity')
    return Vue.$http
      .put(`admin/opportunities/${id}/touch`)
      .then(response => commit('set', response.data))
  },

  destroy({ commit }, id) {
    this.$authorize('delete', 'Opportunity')

    return Vue.$http.delete(`admin/opportunities/${id}`).then(response => commit('remove', id))
  },

  fetchCounts({ commit, state }, { opportunity, filters } = {}) {
    this.$authorize('read', 'Opportunity')

    filters = filters || state.filters
    opportunity = opportunity || Vue.$route.params.id
    state.counts = {}

    return Vue.$http
      .get(`admin/opportunities/${opportunity}/counts`, {
        params: { opportunity, ...filters },
      })
      .then(response => commit('setCounts', response.data))
  },
}

export default {
  namespaced: true,
  modules: {
    locations,
    phases,
    phaseBehaviors,
    questions,
    requirements,
  },
  state,
  getters,
  mutations,
  actions,
}
